import { c as _c } from "react/compiler-runtime";
import { Form, Formik } from 'formik';
import { useContext, useState } from 'react';
import { Grid, GridUnit } from '@hookdeck/theme';
import { isFreePlan } from '../../../../utils/subscription';
import { useCopyToClipboard } from '../../../../utils/copy';
import Badge from '../../../common/base/Badge';
import Button from '../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../common/base/Card';
import Container from '../../../common/base/Container';
import Divider from '../../../common/base/Divider';
import Icon from '../../../common/base/Icon';
import Link from '../../../common/base/Link';
import Tabs from '../../../common/base/Tabs';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import { useDialog } from '../../../common/Dialog';
import RadioCardsInput from '../../../common/Form/Fields/RadioCardsInput';
import SelectInput from '../../../common/Form/Fields/SelectInput';
import SwitchInput, { StyledSwitch } from '../../../common/Form/Fields/SwitchInput';
import TextInput from '../../../common/Form/Fields/TextInput';
import { Div } from '../../../common/helpers/StyledUtils';
import { useToasts } from '../../../common/Toast';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { DashboardContext } from '../../DashboardScene/DashboardContext';
import { OutpostContext, useOutpostConfigUpdate } from '../OutpostContext';
import { OperatorConfig, OperatorConfigKey, OperatorConfigUpdateInput } from '../../../../../../../typings/OutpostDeployment.interface';
const SIGNATURE_DEFAULTS = {
  DESTINATIONS_WEBHOOK_SIGNATURE_ALGORITHM: 'hmac-sha256',
  DESTINATIONS_WEBHOOK_SIGNATURE_ENCODING: 'hex',
  DESTINATIONS_WEBHOOK_SIGNATURE_CONTENT_TEMPLATE: '{{.Body}}',
  DESTINATIONS_WEBHOOK_SIGNATURE_HEADER_TEMPLATE: 'v0={{.Signatures | join ","}}',
  DESTINATIONS_WEBHOOK_SIGNING_SECRET_TEMPLATE: 'whsec_{{.RandomHex}}'
};
const hasCustomSignatureFormat = (config: OperatorConfig) => Object.entries(SIGNATURE_DEFAULTS).some(([key, default_value]) => !!config[key] && config[key] !== default_value);

// *_HEADER_NAME semantics: '' disables the header, non-empty = custom name,
// unset (null) = binary default (enabled, prefixed name).
const isSystemHeaderEnabled = (header_name: string | null) => header_name !== '';
const GeneralSection = () => {
  const $ = _c(9);
  const {
    config,
    updateConfig
  } = useOutpostConfigUpdate();
  const t0 = config.DELIVERY_TIMEOUT_SECONDS ?? "";
  const t1 = config.HTTP_USER_AGENT ?? "";
  const t2 = config.DESTINATIONS_PROXY_URL ?? "";
  let t3;
  if ($[0] !== t0 || $[1] !== t1 || $[2] !== t2) {
    t3 = {
      DELIVERY_TIMEOUT_SECONDS: t0,
      HTTP_USER_AGENT: t1,
      DESTINATIONS_PROXY_URL: t2
    };
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== updateConfig) {
    t4 = async (values, t5) => {
      const {
        resetForm
      } = t5;
      const updated_config = {
        DELIVERY_TIMEOUT_SECONDS: values.DELIVERY_TIMEOUT_SECONDS || null,
        HTTP_USER_AGENT: values.HTTP_USER_AGENT || null,
        DESTINATIONS_PROXY_URL: values.DESTINATIONS_PROXY_URL || null
      };
      const result = await updateConfig(updated_config, {
        success: "Delivery settings updated",
        failure: "Failed to update delivery settings",
        labels: {
          DESTINATIONS_PROXY_URL: "Proxy URL"
        }
      });
      if (!result) {
        return;
      }
      resetForm({
        values: {
          DELIVERY_TIMEOUT_SECONDS: result.DELIVERY_TIMEOUT_SECONDS ?? "",
          HTTP_USER_AGENT: result.HTTP_USER_AGENT ?? "",
          DESTINATIONS_PROXY_URL: result.DESTINATIONS_PROXY_URL ?? ""
        }
      });
    };
    $[4] = updateConfig;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <Formik enableReinitialize={true} initialValues={t3} onSubmit={t4}>{_temp}</Formik>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
const WebhookTab = () => {
  const $ = _c(30);
  const {
    config,
    updateConfig
  } = useOutpostConfigUpdate();
  const t0 = config.DESTINATIONS_WEBHOOK_MODE ?? "";
  const t1 = !!config.DESTINATIONS_WEBHOOK_HEADER_PREFIX;
  const t2 = config.DESTINATIONS_WEBHOOK_HEADER_PREFIX ?? "";
  let t3;
  if ($[0] !== config.DESTINATIONS_WEBHOOK_EVENT_ID_HEADER_NAME) {
    t3 = isSystemHeaderEnabled(config.DESTINATIONS_WEBHOOK_EVENT_ID_HEADER_NAME);
    $[0] = config.DESTINATIONS_WEBHOOK_EVENT_ID_HEADER_NAME;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== config.DESTINATIONS_WEBHOOK_SIGNATURE_HEADER_NAME) {
    t4 = isSystemHeaderEnabled(config.DESTINATIONS_WEBHOOK_SIGNATURE_HEADER_NAME);
    $[2] = config.DESTINATIONS_WEBHOOK_SIGNATURE_HEADER_NAME;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== config.DESTINATIONS_WEBHOOK_TIMESTAMP_HEADER_NAME) {
    t5 = isSystemHeaderEnabled(config.DESTINATIONS_WEBHOOK_TIMESTAMP_HEADER_NAME);
    $[4] = config.DESTINATIONS_WEBHOOK_TIMESTAMP_HEADER_NAME;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== config.DESTINATIONS_WEBHOOK_TOPIC_HEADER_NAME) {
    t6 = isSystemHeaderEnabled(config.DESTINATIONS_WEBHOOK_TOPIC_HEADER_NAME);
    $[6] = config.DESTINATIONS_WEBHOOK_TOPIC_HEADER_NAME;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== config) {
    t7 = hasCustomSignatureFormat(config);
    $[8] = config;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = config.DESTINATIONS_WEBHOOK_SIGNATURE_ALGORITHM ?? "";
  const t9 = config.DESTINATIONS_WEBHOOK_SIGNATURE_ENCODING ?? "";
  const t10 = config.DESTINATIONS_WEBHOOK_SIGNATURE_CONTENT_TEMPLATE ?? "";
  const t11 = config.DESTINATIONS_WEBHOOK_SIGNATURE_HEADER_TEMPLATE ?? "";
  const t12 = config.DESTINATIONS_WEBHOOK_SIGNING_SECRET_TEMPLATE ?? "";
  let t13;
  if ($[10] !== t0 || $[11] !== t1 || $[12] !== t10 || $[13] !== t11 || $[14] !== t12 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7 || $[21] !== t8 || $[22] !== t9) {
    t13 = {
      DESTINATIONS_WEBHOOK_MODE: t0,
      enable_headers_prefix: t1,
      DESTINATIONS_WEBHOOK_HEADER_PREFIX: t2,
      DESTINATIONS_WEBHOOK_DEFAULT_EVENT_ID_HEADER: t3,
      DESTINATIONS_WEBHOOK_DEFAULT_SIGNATURE_HEADER: t4,
      DESTINATIONS_WEBHOOK_DEFAULT_TIMESTAMP_HEADER: t5,
      DESTINATIONS_WEBHOOK_DEFAULT_TOPIC_HEADER: t6,
      customize_signature_format: t7,
      DESTINATIONS_WEBHOOK_SIGNATURE_ALGORITHM: t8,
      DESTINATIONS_WEBHOOK_SIGNATURE_ENCODING: t9,
      DESTINATIONS_WEBHOOK_SIGNATURE_CONTENT_TEMPLATE: t10,
      DESTINATIONS_WEBHOOK_SIGNATURE_HEADER_TEMPLATE: t11,
      DESTINATIONS_WEBHOOK_SIGNING_SECRET_TEMPLATE: t12
    };
    $[10] = t0;
    $[11] = t1;
    $[12] = t10;
    $[13] = t11;
    $[14] = t12;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== config || $[25] !== updateConfig) {
    t14 = async (values, t15) => {
      const {
        resetForm
      } = t15;
      const updated_config = {};
      const is_standard = values.DESTINATIONS_WEBHOOK_MODE === "standard";
      updated_config.DESTINATIONS_WEBHOOK_MODE = values.DESTINATIONS_WEBHOOK_MODE !== "default" ? values.DESTINATIONS_WEBHOOK_MODE : null;
      if (!is_standard) {
        updated_config.DESTINATIONS_WEBHOOK_HEADER_PREFIX = values.enable_headers_prefix && values.DESTINATIONS_WEBHOOK_HEADER_PREFIX ? values.DESTINATIONS_WEBHOOK_HEADER_PREFIX : null;
        const applyHeaderToggle = (enabled, name_key) => {
          if (!enabled) {
            updated_config[name_key] = "";
          } else {
            if (!config[name_key]) {
              updated_config[name_key] = null;
            }
          }
        };
        applyHeaderToggle(values.DESTINATIONS_WEBHOOK_DEFAULT_EVENT_ID_HEADER, "DESTINATIONS_WEBHOOK_EVENT_ID_HEADER_NAME");
        applyHeaderToggle(values.DESTINATIONS_WEBHOOK_DEFAULT_SIGNATURE_HEADER, "DESTINATIONS_WEBHOOK_SIGNATURE_HEADER_NAME");
        applyHeaderToggle(values.DESTINATIONS_WEBHOOK_DEFAULT_TIMESTAMP_HEADER, "DESTINATIONS_WEBHOOK_TIMESTAMP_HEADER_NAME");
        applyHeaderToggle(values.DESTINATIONS_WEBHOOK_DEFAULT_TOPIC_HEADER, "DESTINATIONS_WEBHOOK_TOPIC_HEADER_NAME");
        if (values.customize_signature_format) {
          updated_config.DESTINATIONS_WEBHOOK_SIGNATURE_ALGORITHM = values.DESTINATIONS_WEBHOOK_SIGNATURE_ALGORITHM;
          updated_config.DESTINATIONS_WEBHOOK_SIGNATURE_ENCODING = values.DESTINATIONS_WEBHOOK_SIGNATURE_ENCODING;
          updated_config.DESTINATIONS_WEBHOOK_SIGNATURE_CONTENT_TEMPLATE = values.DESTINATIONS_WEBHOOK_SIGNATURE_CONTENT_TEMPLATE;
          updated_config.DESTINATIONS_WEBHOOK_SIGNATURE_HEADER_TEMPLATE = values.DESTINATIONS_WEBHOOK_SIGNATURE_HEADER_TEMPLATE;
        } else {
          updated_config.DESTINATIONS_WEBHOOK_SIGNATURE_ALGORITHM = null;
          updated_config.DESTINATIONS_WEBHOOK_SIGNATURE_ENCODING = null;
          updated_config.DESTINATIONS_WEBHOOK_SIGNATURE_CONTENT_TEMPLATE = null;
          updated_config.DESTINATIONS_WEBHOOK_SIGNATURE_HEADER_TEMPLATE = null;
        }
        updated_config.DESTINATIONS_WEBHOOK_SIGNING_SECRET_TEMPLATE = values.DESTINATIONS_WEBHOOK_SIGNING_SECRET_TEMPLATE || null;
      }
      const result = await updateConfig(updated_config, {
        success: "Webhook settings updated",
        failure: "Failed to update webhook settings"
      });
      if (!result) {
        return;
      }
      resetForm({
        values: {
          DESTINATIONS_WEBHOOK_MODE: result.DESTINATIONS_WEBHOOK_MODE ?? "",
          enable_headers_prefix: !!result.DESTINATIONS_WEBHOOK_HEADER_PREFIX,
          DESTINATIONS_WEBHOOK_HEADER_PREFIX: result.DESTINATIONS_WEBHOOK_HEADER_PREFIX ?? "",
          DESTINATIONS_WEBHOOK_DEFAULT_EVENT_ID_HEADER: isSystemHeaderEnabled(result.DESTINATIONS_WEBHOOK_EVENT_ID_HEADER_NAME),
          DESTINATIONS_WEBHOOK_DEFAULT_SIGNATURE_HEADER: isSystemHeaderEnabled(result.DESTINATIONS_WEBHOOK_SIGNATURE_HEADER_NAME),
          DESTINATIONS_WEBHOOK_DEFAULT_TIMESTAMP_HEADER: isSystemHeaderEnabled(result.DESTINATIONS_WEBHOOK_TIMESTAMP_HEADER_NAME),
          DESTINATIONS_WEBHOOK_DEFAULT_TOPIC_HEADER: isSystemHeaderEnabled(result.DESTINATIONS_WEBHOOK_TOPIC_HEADER_NAME),
          customize_signature_format: hasCustomSignatureFormat(result),
          DESTINATIONS_WEBHOOK_SIGNATURE_ALGORITHM: result.DESTINATIONS_WEBHOOK_SIGNATURE_ALGORITHM ?? "",
          DESTINATIONS_WEBHOOK_SIGNATURE_ENCODING: result.DESTINATIONS_WEBHOOK_SIGNATURE_ENCODING ?? "",
          DESTINATIONS_WEBHOOK_SIGNATURE_CONTENT_TEMPLATE: result.DESTINATIONS_WEBHOOK_SIGNATURE_CONTENT_TEMPLATE ?? "",
          DESTINATIONS_WEBHOOK_SIGNATURE_HEADER_TEMPLATE: result.DESTINATIONS_WEBHOOK_SIGNATURE_HEADER_TEMPLATE ?? "",
          DESTINATIONS_WEBHOOK_SIGNING_SECRET_TEMPLATE: result.DESTINATIONS_WEBHOOK_SIGNING_SECRET_TEMPLATE ?? ""
        }
      });
    };
    $[24] = config;
    $[25] = updateConfig;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t13 || $[28] !== t14) {
    t15 = <Formik enableReinitialize={true} initialValues={t13} onSubmit={t14}>{_temp3}</Formik>;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  return t15;
};
const AWSKinesisTab = () => {
  const $ = _c(7);
  const {
    config,
    updateConfig
  } = useOutpostConfigUpdate();
  const t0 = config.DESTINATIONS_AWS_KINESIS_METADATA_IN_PAYLOAD === "true";
  let t1;
  if ($[0] !== t0) {
    t1 = {
      DESTINATIONS_AWS_KINESIS_METADATA_IN_PAYLOAD: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== updateConfig) {
    t2 = async (values, t3) => {
      const {
        resetForm
      } = t3;
      const updated_config = {
        DESTINATIONS_AWS_KINESIS_METADATA_IN_PAYLOAD: values.DESTINATIONS_AWS_KINESIS_METADATA_IN_PAYLOAD ? "true" : "false"
      };
      const result = await updateConfig(updated_config, {
        success: "AWS Kinesis settings updated",
        failure: "Failed to update AWS Kinesis settings"
      });
      if (!result) {
        return;
      }
      resetForm({
        values: {
          DESTINATIONS_AWS_KINESIS_METADATA_IN_PAYLOAD: result.DESTINATIONS_AWS_KINESIS_METADATA_IN_PAYLOAD === "true"
        }
      });
    };
    $[2] = updateConfig;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <Formik enableReinitialize={true} initialValues={t1} onSubmit={t2}>{_temp4}</Formik>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const StaticIPSection = () => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    organization,
    team,
    teams,
    mutateTeam,
    mutateTeams,
    subscription,
    team_role
  } = useContext(DashboardContext);
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  const copyToClipboard = useCopyToClipboard();
  const {
    status,
    refreshStatus
  } = useContext(OutpostContext);
  const static_ips = status?.static_ip?.ips ?? [];
  const [is_toggling, setIsToggling] = useState(false);
  const static_ip_enabled_count = teams?.filter(t => t.static_ip_proxy).length || 0;
  const is_free_plan = !!subscription?.plan && isFreePlan(subscription.plan);
  const is_custom_enterprise = !!organization?.feature_flags?.disable_throughput_change;
  const is_admin = team_role === 'admin';
  const can_self_serve = !is_free_plan && !is_custom_enterprise && is_admin;
  const is_addon_enabled_org = static_ip_enabled_count > 0;
  const handleToggle = async (enabled: boolean) => {
    if (enabled) {
      setIsToggling(true);
      try {
        await HookdeckAPI.billing.enableAddonBulk('static_ip', [team!.id]);
        mutateTeam({
          ...team!,
          static_ip_proxy: true
        });
        mutateTeams?.();
        await refreshStatus();
        addToast('success', `Static IP delivery enabled for ${team!.name}.`);
      } catch (error: any) {
        const error_message = error?.response?.message || 'Failed to enable Static IP. Please try again.';
        addToast('error', error_message);
      } finally {
        setIsToggling(false);
      }
    } else {
      showDialog(async () => {
        setIsToggling(true);
        try {
          await HookdeckAPI.billing.disableAddonBulk('static_ip', [team!.id]);
          mutateTeam({
            ...team!,
            static_ip_proxy: false
          });
          await (mutateTeams as () => Promise<unknown>)?.();
          await refreshStatus();
          addToast('success', `Static IP delivery disabled for ${team!.name}.`);
        } catch (error_0: any) {
          const error_message_0 = error_0?.response?.message || 'Failed to disable Static IP. Please try again.';
          addToast('error', error_message_0);
          throw error_0;
        } finally {
          setIsToggling(false);
        }
      }, undefined, {
        title: 'Disable Static IP delivery',
        submit_label: static_ip_enabled_count === 1 ? 'Disable Static IP delivery' : 'Disable for 1 project',
        cancel_label: 'Cancel',
        danger: true,
        message: <Div>
              <Text as="p" m={0}>
                Are you sure you&apos;d like to disable Static IP delivery on{' '}
                <Text as="span" semi>
                  {team!.name}
                </Text>
                ? If destinations on this project have IP allowlisting configured, webhook
                deliveries may begin to fail.
              </Text>
            </Div>
      });
    }
  };
  return <>
      <Text bold size="l" as="h2" m={{
      b: 0
    }}>
        Static IP
      </Text>
      <Text m={{
      b: 4
    }} muted>
        Send deliveries from dedicated static IPs
      </Text>
      <StyledCard m={{
      b: 4
    }}>
        <StyledCardSection flex={{
        justify: 'space-between',
        align: 'center'
      }} p={{
        x: 4,
        y: 3
      }}>
          <Div flex={{
          align: 'center',
          gap: 3
        }}>
            {can_self_serve ? <StyledSwitch as="label" has_label={false} disabled={is_toggling || can_self_serve && !is_addon_enabled_org} onClick={e => {
            e.preventDefault();
            handleToggle(!team!.static_ip_proxy);
          }}>
                <input checked={team!.static_ip_proxy} disabled={is_toggling || can_self_serve && !is_addon_enabled_org} type="checkbox" readOnly />
              </StyledSwitch> : <Tooltip tooltip={is_free_plan ? 'Static IP is a paid feature. Upgrade to a paid plan to enable.' : !is_admin ? 'Only project admins can toggle Static IP delivery.' : undefined} disabled={is_custom_enterprise}>
                <StyledSwitch as="label" has_label={false} disabled>
                  <input checked={team!.static_ip_proxy} disabled type="checkbox" />
                </StyledSwitch>
              </Tooltip>}
            <Div flex={{
            align: 'center',
            gap: 1
          }}>
              <Text shimmering={is_toggling}>Delivery from Static IPs</Text>
              <Tooltip tooltip="Static IPs are used to deliver events through a specific set of IPs that can be allowlisted.">
                <Icon muted icon="info" />
              </Tooltip>
            </Div>
          </Div>
          {is_free_plan && !is_custom_enterprise && <Link to="/settings/organization/plans" icon="link">
              Upgrade plan
            </Link>}
          {can_self_serve && !is_addon_enabled_org && <Link to="/settings/organization/billing/addons/static_ip/enable" icon="link">
              Enable via Billing
            </Link>}
        </StyledCardSection>
        {team!.static_ip_proxy && <StyledCardSection p={4}>
            <StyledCard overflow_hidden>
              <StyledCardSection muted p={3} flex={{
            direction: 'column',
            align: 'flex-start',
            gap: 3
          }}>
                <Div w={100} flex={{
              align: 'center',
              justify: 'space-between',
              gap: 2
            }}>
                  <Text semi size="s">
                    Dedicated Hookdeck IP addresses
                  </Text>
                  <Button ghost icon="copy" m={-1} onClick={() => copyToClipboard(static_ips.join(', '))} />
                </Div>
                <Div flex={{
              align: 'flex-start',
              gap: 2
            }}>
                  {static_ips.map(address => <Badge mono subtle neutral key={address}>
                      {address}
                    </Badge>)}
                </Div>
              </StyledCardSection>
            </StyledCard>
          </StyledCardSection>}
      </StyledCard>
    </>;
};
const DestinationsSettings = () => {
  const $ = _c(17);
  const [active_tab, set_active_tab] = useState("webhook");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Container medium={true} left={true}><GeneralSection /></Container>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Div m={{
      x: 6,
      y: 6
    }}><Divider /></Div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Container medium={true} left={true}><StaticIPSection /></Container>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Div m={{
      x: 6,
      y: 6
    }}><Divider /></Div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Text bold={true} size="l" as="h2" m={{
      b: 0
    }}>Destination Types</Text>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Text m={{
      b: 4
    }} muted={true}>Type-specific delivery configuration.</Text>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [{
      key: "webhook",
      label: "Webhook"
    }, {
      key: "aws_kinesis",
      label: "AWS Kinesis"
    }];
    t7 = {
      b: 4
    };
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  let t8;
  let t9;
  if ($[8] !== active_tab) {
    t8 = <Tabs active_tab={active_tab} onTabSelected={set_active_tab} tabs={t6} m={t7} />;
    t9 = active_tab === "webhook" && <WebhookTab />;
    $[8] = active_tab;
    $[9] = t8;
    $[10] = t9;
  } else {
    t8 = $[9];
    t9 = $[10];
  }
  let t10;
  if ($[11] !== active_tab) {
    t10 = active_tab === "aws_kinesis" && <AWSKinesisTab />;
    $[11] = active_tab;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10 || $[14] !== t8 || $[15] !== t9) {
    t11 = <>{t0}{t1}{t2}{t3}<Container medium={true} left={true}>{t4}{t5}{t8}{t9}{t10}</Container></>;
    $[13] = t10;
    $[14] = t8;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  return t11;
};
export default DestinationsSettings;
function _temp(props) {
  return <Form><Text bold={true} size="l" as="h2" m={{
      t: 0,
      b: 0
    }}>Destinations</Text><Text m={{
      b: 4
    }} muted={true}>Configure delivery behavior and destination types.</Text><TextInput name="DELIVERY_TIMEOUT_SECONDS" label="Delivery timeout (seconds)" help={"Time to wait for a response before marking delivery as failed (1\u2013300)"} type="number" placeholder="30" /><TextInput name="HTTP_USER_AGENT" label="Custom User-Agent" help="Custom User-Agent header for outgoing HTTP requests" placeholder="Outpost" /><TextInput name="DESTINATIONS_PROXY_URL" label="Proxy URL" help="Proxy for outgoing deliveries" placeholder="http://user:pass@proxy.example.com:8080" type="password" /><Button.Permission primary={true} disabled={!props.dirty || props.isSubmitting} submit={true} icon={props.isSubmitting ? "loading" : "save"}>Save</Button.Permission></Form>;
}
function _temp2(header) {
  return <StyledCard m={{
    b: 4
  }} key={header.name}><StyledCardSection p={4} flex={{
      direction: "column",
      gap: 2
    }}><Div flex={{
        align: "center",
        gap: 2
      }}><SwitchInput name={header.name} /><Text>Include <code style={{
            fontWeight: "400"
          }}>{header.header_name}</code>{" "}header</Text></Div></StyledCardSection></StyledCard>;
}
function _temp3(props) {
  const headers_prefix = props.values.enable_headers_prefix && props.values.DESTINATIONS_WEBHOOK_HEADER_PREFIX ? props.values.DESTINATIONS_WEBHOOK_HEADER_PREFIX : "x-outpost-";
  const is_standard_0 = props.values.DESTINATIONS_WEBHOOK_MODE === "standard";
  return <Form><Text semi={true} m={{
      b: 3
    }}>Mode</Text><RadioCardsInput name="DESTINATIONS_WEBHOOK_MODE" m={{
      b: 4
    }} options={[{
      value: "default",
      label: "Default",
      description: "Customizable headers and signature"
    }, {
      value: "standard",
      label: "Standard Webhooks",
      description: "Standard Webhooks specification"
    }]} /><Text semi={true} m={{
      t: 4,
      b: 3
    }}>Header Prefix</Text><StyledCard m={{
      b: 4
    }}><StyledCardSection flex={{
        justify: "flex-start",
        align: "center",
        gap: 3
      }} p={{
        x: 4,
        y: 3
      }}><SwitchInput name="enable_headers_prefix" /><Div flex={{
          align: "center",
          gap: 1
        }}><Text>Customize HTTP headers prefix</Text><Tooltip tooltip={`Customize the prefix for metadata headers. Defaults to ${is_standard_0 ? "\"webhook-\"" : "\"x-outpost-\""}.`}><Icon muted={true} icon="info" /></Tooltip></Div></StyledCardSection>{props.values.enable_headers_prefix && <StyledCardSection p={4}><TextInput m={{
          b: 0
        }} label="Headers Prefix" name="DESTINATIONS_WEBHOOK_HEADER_PREFIX" help={props.values.DESTINATIONS_WEBHOOK_HEADER_PREFIX ? `${props.values.DESTINATIONS_WEBHOOK_HEADER_PREFIX}signature` : `e.g. "${is_standard_0 ? "webhook-" : "x-outpost-"}" → ${is_standard_0 ? "webhook-signature" : "x-outpost-signature"}`} placeholder={is_standard_0 ? "webhook-" : "x-outpost-"} /></StyledCardSection>}</StyledCard>{!is_standard_0 && <><Text semi={true} size="m" m={{
        t: 4,
        b: 3
      }}>Default Headers</Text>{[{
        name: "DESTINATIONS_WEBHOOK_DEFAULT_EVENT_ID_HEADER",
        header_name: `${headers_prefix}event-id`
      }, {
        name: "DESTINATIONS_WEBHOOK_DEFAULT_SIGNATURE_HEADER",
        header_name: `${headers_prefix}signature`
      }, {
        name: "DESTINATIONS_WEBHOOK_DEFAULT_TIMESTAMP_HEADER",
        header_name: `${headers_prefix}timestamp`
      }, {
        name: "DESTINATIONS_WEBHOOK_DEFAULT_TOPIC_HEADER",
        header_name: `${headers_prefix}topic`
      }].map(_temp2)}<Divider m={{
        y: 6
      }} /><Text semi={true} m={{
        b: 3
      }}>Signature</Text><StyledCard m={{
        b: 4
      }}><StyledCardSection flex={{
          justify: "flex-start",
          align: "center",
          gap: 3
        }} p={{
          x: 4,
          y: 3
        }}><SwitchInput name="customize_signature_format" /><Div flex={{
            align: "center",
            gap: 1
          }}><Text>Customize signature format</Text><Tooltip tooltip="Customize the signature format for webhook requests. Defaults to HMAC SHA-256 and Hex encoding."><Icon muted={true} icon="info" /></Tooltip></Div></StyledCardSection>{props.values.customize_signature_format && <StyledCardSection p={4}><Grid gap={4}><GridUnit size={0.5}><SelectInput block={true} name="DESTINATIONS_WEBHOOK_SIGNATURE_ALGORITHM" label="Signature Algorithm" help="Signing algorithm for webhook requests" placeholder="Select algorithm" options={[{
                label: "HMAC SHA-256",
                value: "hmac-sha256"
              }, {
                label: "HMAC SHA-1",
                value: "hmac-sha1"
              }]} /></GridUnit><GridUnit size={0.5}><SelectInput block={true} name="DESTINATIONS_WEBHOOK_SIGNATURE_ENCODING" label="Signature Encoding" help="Signature encoding format" placeholder="Select encoding" options={[{
                label: "Hex",
                value: "hex"
              }, {
                label: "Base64",
                value: "base64"
              }]} /></GridUnit></Grid><TextInput name="DESTINATIONS_WEBHOOK_SIGNATURE_CONTENT_TEMPLATE" label="Signature Content Template" help="Go template for signed content" placeholder="{{.Body}}" mono={true} /><TextInput name="DESTINATIONS_WEBHOOK_SIGNATURE_HEADER_TEMPLATE" label="Signature Header Template" help="Go template for signature header value" placeholder={"v0={{.Signatures | join \",\"}}"} mono={true} /><TextInput m={0} name="DESTINATIONS_WEBHOOK_SIGNING_SECRET_TEMPLATE" label="Signing Secret Template" help="Go template for auto-generated signing secrets (e.g., whsec_{{.RandomHex}})" placeholder="{{.RandomHex}}" mono={true} /></StyledCardSection>}</StyledCard></>}<Button.Permission primary={true} disabled={!props.dirty || props.isSubmitting} submit={true} icon={props.isSubmitting ? "loading" : "save"}>Save</Button.Permission></Form>;
}
function _temp4(props) {
  return <Form><StyledCard m={{
      b: 4
    }}><StyledCardSection flex={{
        justify: "flex-start",
        align: "center",
        gap: 3
      }} p={{
        x: 4,
        y: 3
      }}><SwitchInput name="DESTINATIONS_AWS_KINESIS_METADATA_IN_PAYLOAD" /><Div flex={{
          align: "center",
          gap: 1
        }}><Text>Include metadata in payload</Text><Tooltip tooltip="When enabled, event metadata is included in the Kinesis record payload"><Icon muted={true} icon="info" /></Tooltip></Div></StyledCardSection></StyledCard><Button.Permission primary={true} disabled={!props.dirty || props.isSubmitting} submit={true} icon={props.isSubmitting ? "loading" : "save"}>Save</Button.Permission></Form>;
}